<template>
  <b-row>
    <b-col>
      <h3 class="mb-3">Documents individuels pour la convocation</h3>
      <b-table
        :items="items"
        :fields="fields"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        :busy="isTableLoading"
        show-empty
      >
        <!-- Heads -->
        <template #head(name)>
          <div class="d-flex align-items-center">
            <span class="mr-2 cursor-pointer" @click="setSortByName()">Nom</span>
            <font-awesome-icon
              v-if="sortByName == 'DESC'"
              icon="sort-down"
              @click="setSortByName()"
            ></font-awesome-icon>
            <font-awesome-icon
              v-else-if="sortByName == 'ASC'"
              icon="sort-up"
              @click="setSortByName()"
            ></font-awesome-icon>
          </div>
        </template>
        <template #head(agency_preferred_broadcast)="field">
          <div v-show="showAgencyPreferredBroadcast && isMeetingSendingPvToAslCoOwners">
            {{ field.label }}
          </div>
        </template>
        <template #head(agency_preferred_invitation_broadcast)="field">
          <div
            v-show="
              showAgencyPreferredInvitationBroadcastField && isMeetingSendingInvitationToAslCoOwners
            "
          >
            {{ field.label }}
          </div>
        </template>
        <template #head(cogeprint)="field">
          <div v-show="meetingStatus != meetingStatuses.mandatesCreated">
            {{ field.label }}
          </div>
        </template>
        <!-- Cells -->
        <template #cell(index)="data">
          {{ pageIncrementalIndex + data.index + 1 }}
        </template>
        <template #cell(gender)="data">
          <span>{{ data.item.gender }}</span>
        </template>
        <template #cell(name)="data">
          <div class="d-flex flex-column">
            <div class="font-10">{{ data.item.representantName }}</div>
            <div>{{ data.item.name }}</div>
          </div>
        </template>
        <template #cell(documents)="data">
          <b-badge
            v-if="data.item.documents && data.item.documents.mergedInvitationMedia"
            variant="info"
            class="mr-1"
            pill
          >
            <b-link
              class="small text-white text-decoration-none"
              target="_blank"
              :href="data.item.documents.mergedInvitationMedia"
            >
              <font-awesome-icon icon="file-pdf" size="1x" />
              L'invitation à la convocation, fusionné
            </b-link>
          </b-badge>
          <b-badge
            v-if="data.item.documents && data.item.documents.mergedPvMedia"
            variant="dark"
            class="mr-1"
            pill
          >
            <b-link
              class="small text-white text-decoration-none"
              target="_blank"
              :href="data.item.documents.mergedPvMedia"
            >
              <font-awesome-icon icon="file-pdf" size="1x" />
              Le procès-verbal, fusionné
            </b-link>
          </b-badge>
        </template>
        <template #cell(agency_preferred_broadcast)="data">
          <v-select
            v-show="showAgencyPreferredBroadcast && isMeetingSendingPvToAslCoOwners"
            v-model="data.item.agency_preferred_broadcast"
            class="select-list-width-auto"
            :options="agencyPreferredPvBroadcastOptions(data.item)"
            :clearable="false"
            :disabled="isAgencyPreferredBroadcastDisabled"
            @input="updatePrefferedBroadcast($event, data.item)"
          >
            <span slot="no-options">
              {{ $t("general.select_with_input_no_option") }}
            </span>
          </v-select>
        </template>

        <template #cell(agency_preferred_invitation_broadcast)="data">
          <v-select
            v-show="
              showAgencyPreferredInvitationBroadcastField && isMeetingSendingInvitationToAslCoOwners
            "
            v-model="data.item.agency_preferred_invitation_broadcast"
            class="select-list-width-auto"
            :options="agencyPreferredInvitationBroadcastOptions(data.item)"
            :clearable="false"
            :disabled="isSelectedPreferredInvitationDisabled"
            @input="updatePrefferedInvitationBroadcast($event, data.item)"
          >
            <span slot="no-options">
              {{ $t("general.select_with_input_no_option") }}
            </span>
          </v-select>
        </template>

        <template #cell(cogeprint)="data">
          <div v-show="meetingStatus != meetingStatuses.mandatesCreated">
            <font-awesome-icon
              class="cursor-pointer"
              icon="info-circle"
              size="1x"
              @click="toggleDetails(data)"
            />
            <span v-if="data.item.docsStatus && data.item.docsStatus.length > 0" class="ml-2">
              {{ getLastDocStatusDetails(data.item.docsStatus) }}
            </span>
            <span v-if="data.item.docsStatus && data.item.docsStatus.length > 0" class="font-10">
              {{ " (" + getLastDocStatusDateTime(data.item.docsStatus) + ")" }}
            </span>
          </div>
        </template>

        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle mr-2"></b-spinner>
            <strong>Traitement en cours...</strong>
          </div>
        </template>

        <template #empty>
          <div class="text-center my-4">
            <p v-if="searchParams && searchParams.trim() !== ''" class="text-muted">
              <font-awesome-icon icon="search" size="2x" class="mb-2" />
              <br />
              Aucun résultat trouvé pour "{{ searchParams }}"
            </p>
            <p v-else class="text-muted">
              Aucun document disponible
            </p>
          </div>
        </template>

        <template #row-details="data">
          <appDocsStatuses
            :data-item="data.item"
            :mandates="[]"
            :recipient-meeting-docs="recipientMeetingDocs"
          ></appDocsStatuses>
        </template>
      </b-table>
    </b-col>
    <b-col cols="12" class="text-center">
      <b-col cols="4" class="m-auto d-flex justify-content-center">
        <b-pagination
          v-model="pagination.page"
          :total-rows="pagination.rowCount"
          :per-page="pagination.pageSize"
          aria-controls="mt-table"
          @change="changeTablePage"
        ></b-pagination>
      </b-col>
    </b-col>
  </b-row>
</template>

<script>
import RecipientMeetingDocMW from "@/middlewares/RecipientMeetingDoc";

import MeetingORM from "@/store/orm-axios/models/Meeting";
import BuildingOwnerORM from "@/store/orm-axios/models/BuildingOwner";

import { franceAndDOMCountries } from "@/constants/default.js";
import { lreRecordStatus } from "@/constants/mandate.js";
import links from "@/links/links";
export default {
  components: {
    appCogePrintCodes: () => import("./CogePrintCodes"),
    appDocsStatuses: () => import("./MeetingDocumentsDocsStatuses"),
  },
  props: {
    broadcastPvSendOptions: {
      type: Array,
      require: true,
    },
    broadcastInvitationSendOption: {
      type: Array,
      require: true,
    },
    showAgencyPreferredBroadcast: {
      type: Boolean,
      require: true,
    },
    isAgencyPreferredBroadcastDisabled: {
      type: Boolean,
      required: true,
    },
    isSelectedPreferredInvitationDisabled: {
      type: Boolean,
      require: true,
    },
    showAgencyPreferredInvitationBroadcastField: {
      type: Boolean,
      require: true,
    },
    meetingStatus: {
      type: Number,
      require: true,
    },
    isMeetingSendingInvitationToAslCoOwners: {
      type: Boolean,
      default: false,
    },
    isMeetingSendingPvToAslCoOwners: {
      type: Boolean,
      default: false,
    },
    searchParams: {
      type: String,
      default: "",
    },
  },
  data: function () {
    return {
      franceAndDOMCountries,
      links,
      recipientMeetingDocs: [],
      items: [],
      isTableLoading: false,
      sortBy: "name",
      sortByName: "ASC",
      sortDesc: false,
      pagination: {
        page: 1,
        pageSize: 10,
        rowCount: 0,
        pageCount: 0,
      },
      fields: [
        { key: "index", label: "Nᵒ" },
        { key: "gender", label: "Civilité", sortable: false },
        { key: "name", label: "Nom", sortable: false },
        {
          key: "documents",
          label: "Documents individuels du copropriétaire",
        },
        {
          key: "agency_preferred_invitation_broadcast",
          label: "Canal de diffusion l'invitation",
          sortable: false,
        },
        {
          key: "agency_preferred_broadcast",
          label: "Canal de diffusion pour le PV",
          sortable: false,
        },

        { key: "cogeprint", label: "État de l'impression", sortable: false },
      ],
    };
  },
  computed: {
    meeting() {
      return MeetingORM.find(this.$route.params.id);
    },
    pageIncrementalIndex() {
      return (this.pagination.page - 1) * this.pagination.pageSize;
    },
  },
  async created() {
    await this.getRecipientMeetingDocs();
    this.processItems();
  },
  methods: {
    async refreshData() {
      console.log('refreshData called, searchParams:', this.searchParams);
      await this.getRecipientMeetingDocs();
      this.processItems();
    },
    async getRecipientMeetingDocs() {
      this.isTableLoading = true;
      
      try {
        const queryParams = {
          meetingId: this.$route.params.id,
          page: this.pagination.page,
          itemsPerPage: this.pagination.pageSize,
          orderByName: this.sortByName,
          searchParams: this.searchParams,
        };
        await RecipientMeetingDocMW.GET_FOR_ASL(queryParams)
          .then(res => {
            if (res.response && res.response.data && res.response.data["hydra:member"]) {
              this.recipientMeetingDocs = res.response.data["hydra:member"];
            }
            let totalItems = res.response.data["hydra:totalItems"];
            this.pagination.rowCount = totalItems;
          })
          .catch(err => {
            console.error("Error fetching recipient meeting docs:", err);
            this.recipientMeetingDocs = [];
            this.pagination.rowCount = 0;
          });
      } finally {
        this.isTableLoading = false;
      }
    },
    processItems() {
      this.items = []; // Clear items before processing
      
      this.recipientMeetingDocs.forEach(recipientMeetingDoc => {
        let recipient = {};

        const broadcastSendOptionRecipient = this.broadcastPvSendOptions.filter(
          broadcastOption =>
            broadcastOption.value === recipientMeetingDoc.agency_preferred_broadcast
        );
        let broadcastInvitationSendOptionRecipient = this.broadcastInvitationSendOption.filter(
          broadcastOption =>
            broadcastOption.value === recipientMeetingDoc.agency_preferred_invitation_broadcast
        );

        const buildingOwner = BuildingOwnerORM.find(
          recipientMeetingDoc.recipient.building_owner_id
        );

        recipient.representantName = buildingOwner ? this.getPersonName({}, buildingOwner) : "";
        recipient.name = this.getPersonName({}, recipientMeetingDoc.recipient);
        recipient.email = recipientMeetingDoc.recipient.email;
        recipient.country = recipientMeetingDoc.recipient.country;
        // Get LRE agreement from building owner settings
        recipient.lre_agreement = buildingOwner && buildingOwner.settings
          ? buildingOwner.settings.lre_agreement
          : null;

        recipient.recipientMeetingDocId = recipientMeetingDoc.id;
        recipient.docsStatus = recipientMeetingDoc.docs_status;
        recipient.agency_preferred_broadcast = broadcastSendOptionRecipient;
        recipient.agency_preferred_invitation_broadcast = broadcastInvitationSendOptionRecipient;

        recipient.documents = [];
        if (recipientMeetingDoc.merged_invitation_media) {
          recipient.documents.mergedInvitationMedia = links.API_WEB_APP_MEDIA_LINK(
            recipientMeetingDoc.merged_invitation_media.file_path
          );
        }
        if (recipientMeetingDoc.merged_pv_media) {
          recipient.documents.mergedPvMedia = links.API_WEB_APP_MEDIA_LINK(
            recipientMeetingDoc.merged_pv_media.file_path
          );
        }

        this.items.push(recipient);
      });
    },
    async changeTablePage(value) {
      this.pagination.page = value;
      this.isTableLoading = true;
      this.items = [];

      await this.getRecipientMeetingDocs();
      this.processItems();

      this.isTableLoading = false;
    },
    async setSortByName() {
      if (this.sortByName == "ASC") {
        this.sortDesc = true;
        this.sortByName = "DESC";
      } else {
        this.sortByName = "ASC";
        this.sortDesc = false;
      }
      this.items = [];

      await this.getRecipientMeetingDocs();
      this.processItems();
    },
    agencyPreferredPvBroadcastOptions(item) {
      let isFrOrDOMCountry = this.$_.contains(this.franceAndDOMCountries, item.country);
      let returnOptions = this.broadcastPvSendOptions;
      if (!item.email) {
        returnOptions = returnOptions.filter(b => b.label != "Email" && b.label != "LRE");
      }
      // Filter out LRE if no LRE consent
      if (!item.lre_agreement) {
        returnOptions = returnOptions.filter(b => b.label != "LRE");
      }
      if (!isFrOrDOMCountry) {
        returnOptions = returnOptions.filter(
          b =>
            b.label != "La Poste - Courrier recommandé avec AR" &&
            b.label != "La Poste - Courrier simple" &&
            b.label != "Colissimo France"
        );
      }
      if (isFrOrDOMCountry) {
        returnOptions = returnOptions.filter(
          b =>
            b.label != "La Poste : Lettre prioritaire internationale" &&
            b.label != "Colissimo International"
        );
      }
      return returnOptions;
    },
    agencyPreferredInvitationBroadcastOptions(item) {
      let isFrOrDOMCountry = this.$_.contains(this.franceAndDOMCountries, item.country);
      let returnOptions = this.broadcastInvitationSendOption;
      if (!item.email) {
        returnOptions = returnOptions.filter(b => b.label != "Email" && b.label != "LRE");
      }
      // Filter out LRE if no LRE consent
      if (!item.lre_agreement) {
        returnOptions = returnOptions.filter(b => b.label != "LRE");
      }
      if (!isFrOrDOMCountry) {
        returnOptions = returnOptions.filter(
          b =>
            b.label != "La Poste - Courrier recommandé avec AR" &&
            b.label != "La Poste - Courrier simple" &&
            b.label != "Colissimo France"
        );
      }
      if (isFrOrDOMCountry) {
        returnOptions = returnOptions.filter(
          b =>
            b.label != "La Poste : Lettre prioritaire internationale" &&
            b.label != "Colissimo International"
        );
      }
      return returnOptions;
    },
    updatePrefferedBroadcast(agencyPreferredBroadcast, item) {
      let recipientMeetingDoc = this.recipientMeetingDocs.find(
        recipientMeetingDoc => recipientMeetingDoc.id == item.recipientMeetingDocId
      );
      if (recipientMeetingDoc) {
        recipientMeetingDoc.agency_preferred_broadcast = agencyPreferredBroadcast.value;
        RecipientMeetingDocMW.PUT(recipientMeetingDoc);
      }
    },
    updatePrefferedInvitationBroadcast(agencyPreferredBroadcast, item) {
      let recipientMeetingDoc = this.recipientMeetingDocs.find(
        recipientMeetingDoc => recipientMeetingDoc.id == item.recipientMeetingDocId
      );
      if (recipientMeetingDoc) {
        recipientMeetingDoc.agency_preferred_invitation_broadcast = agencyPreferredBroadcast.value;
        RecipientMeetingDocMW.PUT(recipientMeetingDoc);
      }
    },
    toggleDetails(data) {
      data.toggleDetails();
    },
    getLastDocStatusDetails(docsStatus) {
      let sortedArray = this.$_.sortBy(docsStatus, status => {
        if (status.DHStatus) {
          return status.DHStatus;
        } else if (status.createdAt) {
          return status.createdAt;
        } else {
          return null;
        }
      });

      sortedArray.reverse();

      if (sortedArray[0].Details) {
        return sortedArray[0].Details;
      } else if (sortedArray[0].recordStatus && sortedArray[0].recordRecipientStatus) {
        return "Status: " + lreRecordStatus[sortedArray[0].recordStatus];
      }
    },
    getLastDocStatusDateTime(docsStatus) {
      let sortedArray = this.$_.sortBy(docsStatus, status => {
        if (status.DHStatus) {
          return status.DHStatus;
        } else if (status.createdAt) {
          return status.createdAt;
        } else {
          return null;
        }
      });

      sortedArray.reverse();

      if (sortedArray[0].DHStatus) {
        return this.$moment(this.toUtc(sortedArray[0].DHStatus)).format("YYYY-MM-DD HH:mm:ss");
      } else if (sortedArray[0].createdAt) {
        return this.$moment(this.toUtc(sortedArray[0].createdAt)).format("YYYY-MM-DD HH:mm:ss");
      }
    },
    // NOTE: This function will make sure that data that has no timezone will have UTC
    toUtc(date) {
      let utcFormatted = date;
      if (date) {
        utcFormatted = this.$moment
          .parseZone(date)
          .utcOffset(0, true)
          .format("YYYY-MM-DDTHH:mm:ssZ");
      }
      return utcFormatted;
    },
  },
};
</script>
